/** Textos legíveis para a revisão de higienização de cadastros. */

import { isUsableStoredPhone } from "../../utils/phone";

export function isWhatsappGroupContactValue(value) {
  const v = String(value ?? "").trim();
  return /^grupo:/i.test(v) || /@g\.us$/i.test(v);
}

export function formatContactValue(value, t) {
  const v = String(value ?? "").trim();
  if (!v || !isUsableStoredPhone(v)) return null;
  if (/^grupo:/i.test(v)) {
    const id = v.replace(/^grupo:/i, "").trim();
    return t("clientHygiene.whatsappGroupKey", { id });
  }
  if (/@g\.us$/i.test(v)) {
    return t("clientHygiene.whatsappGroupJid", { jid: v });
  }
  return v;
}

export function formatHygieneCurrentValue(issue, t) {
  const value = String(issue?.current ?? "").trim();
  if (!value) return null;
  if (t) return formatContactValue(value, t);
  return value;
}

export function formatMatchByLabels(matchedBy, t) {
  const keys = Array.isArray(matchedBy) ? matchedBy : [];
  if (!keys.length) return t("clientHygiene.matchByUnknown");
  return keys
    .map((key) => t(`clientHygiene.matchBy_${key}`, { defaultValue: key }))
    .join(", ");
}

export function duplicateConfidenceChipColor(level) {
  if (level === "high") return "success";
  if (level === "medium") return "warning";
  return "default";
}

export function formatDuplicateConfidenceLabel(level, t) {
  if (level === "high") return t("clientHygiene.confidenceHigh");
  if (level === "medium") return t("clientHygiene.confidenceMedium");
  return t("clientHygiene.confidenceLow");
}

export function getIssueMaxDuplicateConfidence(issue) {
  const dups = issue?.meta?.duplicates || [];
  let maxScore = 0;
  let level = "low";
  for (const dup of dups) {
    const score = Number(dup.score) || 0;
    if (score >= maxScore) {
      maxScore = score;
      level = dup.confidence || level;
    }
  }
  return { level, score: maxScore };
}

export function issueMatchesDuplicateConfidenceFilter(issue, filter) {
  if (filter === "all") return true;
  const { level } = getIssueMaxDuplicateConfidence(issue);
  if (filter === "high") return level === "high";
  if (filter === "medium_plus") return level === "high" || level === "medium";
  return true;
}

export function formatDuplicateIssueSummary(issue, t) {
  const duplicates = issue?.meta?.duplicates || [];
  if (!duplicates.length) {
    return t("clientHygiene.duplicateNoCandidates");
  }
  return duplicates
    .map((dup) =>
      t("clientHygiene.duplicateCandidateLine", {
        id: dup.id,
        nome: dup.nome || `#${dup.id}`,
        reasons: formatMatchByLabels(dup.matchedBy, t),
        confidence: formatDuplicateConfidenceLabel(dup.confidence || "low", t),
      })
    )
    .join("\n");
}

export function formatHygieneSuggestedValue(issue, t) {
  if (issue?.rule === "duplicate_candidate") {
    const duplicates = issue?.meta?.duplicates || [];
    if (!duplicates.length) return t("clientHygiene.duplicateNoCandidates");
    return duplicates
      .map((dup) => `#${dup.id}${dup.nome ? ` ${dup.nome}` : ""}`)
      .join(", ");
  }

  const raw = String(issue?.suggested ?? "").trim();
  if (raw) return raw;

  if (issue?.autoFixable) {
    if (issue.rule === "phone_cross_duplicate") {
      return t("clientHygiene.suggestClearField");
    }
    return t("clientHygiene.suggestAutoApply");
  }

  if (issue?.meta?.message) {
    return issue.meta.message;
  }
  if (issue?.meta?.reason) {
    return issue.meta.reason;
  }

  const manualKey = `clientHygiene.manual_${issue?.rule}`;
  const manual = t(manualKey, { defaultValue: "" });
  if (manual) return manual;

  return t("clientHygiene.suggestManual");
}

export function formatHygieneChangeLine(issue, t) {
  const field = issue?.field || "—";
  const current = formatHygieneCurrentValue(issue, t);
  const suggested = formatHygieneSuggestedValue(issue, t);
  const currentLabel = current ?? t("clientHygiene.valueEmpty");

  if (!issue?.autoFixable) {
    return {
      label: `#${issue.clientId} · ${field}`,
      value: suggested,
      severity: "warning",
    };
  }

  return {
    label: `#${issue.clientId} · ${field}`,
    value: t("clientHygiene.changePreview", { current: currentLabel, suggested }),
    severity: "info",
  };
}

export function isActionableAutoFix(issue) {
  if (!issue?.autoFixable) return false;
  if (isWhatsappGroupContactValue(issue.current)) return false;
  return true;
}

export function buildHygieneApplyChangeLines(issues, selectedIssueIds, t) {
  const selected = new Set(selectedIssueIds);
  return (issues || [])
    .filter((issue) => selected.has(issue.issueId) && isActionableAutoFix(issue))
    .map((issue) => formatHygieneChangeLine(issue, t));
}

function extractMemberFromIssues(issues, memberId) {
  const id = Number(memberId);
  for (const issue of issues || []) {
    if (Number(issue.clientId) === id) {
      const ref = issue.meta?.duplicates?.[0];
      return {
        id,
        nome: ref?.nome || null,
        matchedBy: ref?.matchedBy || [],
        confidence: ref?.confidence || "low",
        score: ref?.score ?? 0,
      };
    }
    for (const dup of issue.meta?.duplicates || []) {
      if (Number(dup.id) === id) {
        return {
          id,
          nome: dup.nome || null,
          matchedBy: dup.matchedBy || [],
          confidence: dup.confidence || "low",
          score: dup.score ?? 0,
        };
      }
    }
  }
  return { id, nome: null, matchedBy: [], confidence: "low", score: 0 };
}

function unionFindConnect(parent, a, b) {
  const find = (x) => {
    if (!parent.has(x)) parent.set(x, x);
    if (parent.get(x) !== x) parent.set(x, find(parent.get(x)));
    return parent.get(x);
  };
  const ra = find(a);
  const rb = find(b);
  if (ra !== rb) parent.set(ra, rb);
}

/** Agrupa issues de duplicata em clusters conectados (evita N linhas repetidas). */
export function buildDuplicateClusters(issues) {
  const dupIssues = (issues || []).filter((i) => i.rule === "duplicate_candidate");
  const parent = new Map();

  for (const issue of dupIssues) {
    const cid = Number(issue.clientId);
    if (!Number.isFinite(cid)) continue;
    unionFindConnect(parent, cid, cid);
    for (const dup of issue.meta?.duplicates || []) {
      const did = Number(dup.id);
      if (Number.isFinite(did)) unionFindConnect(parent, cid, did);
    }
  }

  const find = (x) => {
    if (!parent.has(x)) parent.set(x, x);
    if (parent.get(x) !== x) parent.set(x, find(parent.get(x)));
    return parent.get(x);
  };

  const buckets = new Map();
  for (const id of parent.keys()) {
    const root = find(id);
    if (!buckets.has(root)) buckets.set(root, new Set());
    buckets.get(root).add(id);
  }

  return [...buckets.values()]
    .filter((set) => set.size >= 2)
    .map((memberIds) => {
      const ids = [...memberIds].sort((a, b) => a - b);
      const members = ids.map((id) => extractMemberFromIssues(dupIssues, id));
      const nomeCounts = new Map();
      for (const m of members) {
        const n = String(m.nome || "").trim();
        if (n) nomeCounts.set(n, (nomeCounts.get(n) || 0) + 1);
      }
      let label = `#${ids.join(", #")}`;
      if (nomeCounts.size) {
        label = [...nomeCounts.entries()].sort((a, b) => b[1] - a[1])[0][0];
      }
      const matchedBySet = new Set();
      let maxScore = 0;
      let maxLevel = "low";
      for (const m of members) {
        for (const key of m.matchedBy || []) matchedBySet.add(key);
        if ((m.score || 0) >= maxScore) {
          maxScore = m.score || 0;
          maxLevel = m.confidence || maxLevel;
        }
      }
      return {
        clusterId: ids.join("-"),
        label,
        members,
        memberIds: ids,
        matchedBy: [...matchedBySet],
        confidence: maxLevel,
        score: maxScore,
        defaultPrimaryId: ids[0],
      };
    })
    .sort((a, b) => b.members.length - a.members.length || b.score - a.score);
}

export function clusterMatchesDuplicateConfidenceFilter(cluster, filter) {
  if (filter === "all") return true;
  const level = cluster?.confidence || "low";
  if (filter === "high") return level === "high";
  if (filter === "medium_plus") return level === "high" || level === "medium";
  return true;
}
